import axios from "axios";
import store from "@/store/index.js";
import router from "@/router";
import configURL from "@/config/index.js";
import i18n from "@/locales/index";
import { notification } from "ant-design-vue";

let baseURL = configURL.baseURL;

const Service = axios.create({
  timeout: 10000 * 3,
  baseURL: baseURL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json;charset=UTF-8",
    "Request-Type": "admin",
  },
});
const debounceTokenCancel = new Map();

const REFRESH_URL = "user-service/auth/access-token";
// Один общий refresh на все параллельные 401. Обнуляется после завершения,
// поэтому следующий цикл истечения токена стартует заново.
let refreshPromise = null;

function refreshOnce() {
  if (refreshPromise) return refreshPromise;

  refreshPromise = axios({
    url: baseURL + REFRESH_URL,
    headers: {
      "Content-Type": "application/json;charset=UTF-8",
      "Request-Type": "admin",
    },
    method: "GET",
    data: {
      refreshToken: store.state.userInfo.refreshToken || null,
    },
    withCredentials: true,
  })
    .then((res) => {
      if (res.status == 200 && res.data && res.data.code == 0) {
        let userInfo = JSON.parse(JSON.stringify(store.state.userInfo));
        userInfo["accessToken"] = res.data.data.accessToken;
        userInfo["refreshToken"] = res.data.data.refreshToken;
        store.commit("setUserInfo", userInfo);
        return userInfo["accessToken"];
      }
      throw new Error("refresh_failed");
    })
    .catch((e) => {
      store.commit("setUserInfo", {});
      router.push({
        name: "LoginView",
      });
      throw e;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}
Service.interceptors.request.use(
  (config) => {
    store.commit("setLoading", true);
    let accessToken = store.state.userInfo.accessToken || null;
    if (accessToken && !config.url.includes("login")) {
      config.headers["Authorization"] = "Bearer " + accessToken;
    }
    config.headers["Request-Type"] = "admin";
    config.headers["All-Permission"] = store.state.isGiveAllPermission ? 1 : 0;

    let currentLang = i18n.global.locale.value || "en";
    switch (currentLang) {
      case "kz":
        currentLang = "kk";
        break;
      case "rus":
        currentLang = "ru";
        break;
      case "zh":
        currentLang = "cn";
        break;
      case "en":
        currentLang = "en";
        break;
    }
    config.headers["lang"] = currentLang;
    config.headers["Accept-Language"] = currentLang == "cn" ? "zh" : currentLang;
    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }
    const tokenKey = `${1}-${config.url}`;
    const cancel = debounceTokenCancel.get(tokenKey);
    let allowRepeat = config.url.indexOf("download_file_base64") == -1;
    let ignoreDebounceUrls = ["upload_file", "upload-url"];
    let filter = ignoreDebounceUrls.filter((e) => config.url.indexOf(e) >= 0);
    let ignoreDebounce = filter.length > 0;
    if (cancel && allowRepeat && ignoreDebounce == false) {
      cancel();
    }
    new Promise((resolve) => {
      const timer = setTimeout(() => {
        clearTimeout(timer);

        resolve(config);
      }, 500);
      debounceTokenCancel.set(tokenKey, () => {
        clearTimeout(timer);
        resolve(new Error("Q_msg: Бұл сұраудан бас тартылды / 防抖"));
      });
    });
    return config;
  },
  async (error) => {
    store.commit("setLoading", false);
    return Promise.reject(error);
  }
);
Service.interceptors.response.use(
  async (res) => {
    store.commit("setLoading", false);
    if (
      res.config.responseType === "blob" &&
      res.request.response.type === "application/json"
    ) {
      return {
        result_code: -1,
        blob: res.data,
      };
    }
    if (toString?.call(res.data) === "[object Blob]") {
      const { data, headers } = res;
      let fileName;
      if (headers["content-disposition"]) {
        fileName = headers["content-disposition"].replace(
          /\w+;filename=(.*)/,
          "$1"
        );
      } else {
        fileName = data.fileName;
      }
      return {
        result_code: 0,
        fileName,
        blob: data,
      };
    }

    return res.data;
  },
  async (error) => {
    store.commit("setLoading", false);
    if (!error || !error.response) {
      return false;
    }
    if (error.response.status === 423) {
      store.commit("setSystemBreak", {
        reason: error.response.data?.message || error.response.data?.code,
      });
      return Promise.reject(error);
    }
    if (error.response.status == 401) {
      const cfg = error.config;
      // сам refresh упал — не зацикливаемся
      if (!cfg || String(cfg.url || "").includes(REFRESH_URL)) {
        return Promise.reject(error);
      }
      // запрос уже переигрывали после refresh — сессия действительно мертва
      if (cfg.__isRetry) {
        return Promise.reject(error);
      }
      cfg.__isRetry = true;

      store.commit("setLoading", true);
      try {
        await refreshOnce();
        store.commit("setLoading", false);
        return Service(cfg);
      } catch (e) {
        store.commit("setLoading", false);
        return Promise.reject(error);
      }
    }
    if (error.response.result_msg) {
      toastMessage(error.response.result_msg, "error", error.response.data);
    }
    return Promise.reject(error);
  }
);

function toastMessage(msg, type, data) {
  if (!msg) return;
  let message = i18n.global.t("l_Error");

  if (msg !== null) {
    message = msg;
  }

  if (msg && msg.indexOf("l_") == 0 && msg.indexOf("Success") == -1) {
    if (message.indexOf("l_") == 0) {
      let reg = /[A-Z]+/;
      message = msg.replace("l_", "");
      let arr = message.split("");
      arr.forEach((item, index) => {
        if (index > 0 && reg.test(item)) {
          arr[index] = " " + item.toLowerCase();
        }
      });
      message = arr.join("");
    }
    if (typeof data == "string") {
      message = message + " (" + data + ")";
    }
    notification[type || "warning"]({
      message: message,
    });
  } else if (message) {
    notification[type || "warning"]({
      message: message,
    });
  }
}

export default Service;
